import React from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Stack, Typography, Chip } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import BusinessIcon from '@mui/icons-material/Business';
import logoSrc from 'images/logo.svg';

const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

// Formatea precio sin "$NaN" (mismo criterio que PlanAccordion).
const fmtPrice = (value, currency) => {
  if (value == null || value === '') return null;
  const number = Number(value);
  if (Number.isNaN(number)) return null;
  return currency === 'USD'
    ? `$${number.toFixed(2)} USD`
    : `$${number.toLocaleString('es-CO')} COP`;
};

/**
 * Card de plan PARTIDA y clickeable (monocromo off-black): imagen a la izquierda (arriba en
 * mobile) e información a la derecha. Toda la card navega al detalle del plan. Opcionalmente
 * recibe `actions` (botones Suscribirme/Solicitar) y `badge` (chips de estado) que no propagan
 * el click. Reutilizable en cuentas nuevas (LimitedDashboard) y en "Mis planes".
 */
function PlanSplitCard({ plan, actions = null, badge = null }) {
  const navigate = useNavigate();
  const { t } = useTranslation(['common']);
  const preferredCurrency = useSelector((state) => state.auth.user?.preferred_currency);
  const showCop = preferredCurrency === 'COP' || !preferredCurrency;
  const price = showCop ? fmtPrice(plan.price_cop, 'COP') : fmtPrice(plan.price_usd, 'USD');
  const discount = plan.active_discount_percent || 0;
  // Tarifa corporativa (auto por membresía) del usuario para este plan.
  const orgBenefit = plan.org_benefit && plan.org_benefit.discount_percent > 0 ? plan.org_benefit : null;

  const goToDetail = () => navigate(`/plan-detail/${plan.id}`);
  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToDetail();
    }
  };
  const stop = (e) => e.stopPropagation();

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={plan.name}
      onClick={goToDetail}
      onKeyDown={onKeyDown}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        width: '100%',
        overflow: 'hidden',
        borderRadius: '16px',
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.raised',
        cursor: 'pointer',
        boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow)',
        transition: `transform 0.25s ${EASE}, background-color 0.25s ${EASE}, border-color 0.25s ${EASE}, box-shadow 0.25s ${EASE}`,
        '&:hover': {
          transform: 'translateY(-3px)',
          backgroundColor: 'surface.hover',
          borderColor: 'hairline.strong',
          boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight), var(--otx-card-shadow-hover)'
        },
        '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2 }
      }}>
      {/* Mitad imagen (arriba en mobile) */}
      <Box
        sx={{
          position: 'relative',
          flexShrink: 0,
          width: { xs: '100%', md: '42%' },
          height: { xs: 160, md: 'auto' },
          minHeight: { md: 190 },
          backgroundColor: 'surface.content',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
        {discount > 0 && (
          <Chip
            icon={<LocalOfferIcon sx={{ fontSize: 15, color: '#0A0A0A !important' }} />}
            label={`-${discount}%`}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              zIndex: 2,
              backgroundColor: 'gold.main',
              color: '#0A0A0A',
              fontWeight: 800,
              boxShadow: '0 2px 8px rgba(0,0,0,0.35)'
            }}
          />
        )}
        {orgBenefit && (
          <Chip
            icon={<BusinessIcon sx={{ fontSize: 15, color: '#0A0A0A !important' }} />}
            label={`${orgBenefit.organization_name} · -${orgBenefit.discount_percent}%`}
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              zIndex: 2,
              maxWidth: 'calc(100% - 20px)',
              backgroundColor: 'gold.main',
              color: '#0A0A0A',
              fontWeight: 800,
              boxShadow: '0 2px 8px rgba(0,0,0,0.35)'
            }}
          />
        )}
        {plan.image_url ? (
          <Box
            component="img"
            src={plan.image_url}
            alt={plan.name}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          <Box
            component="img"
            src={logoSrc}
            alt="OTRIX"
            sx={{ height: 64, opacity: 0.35, filter: 'grayscale(1) brightness(1.4)' }}
          />
        )}
      </Box>

      {/* Mitad información */}
      <Stack spacing={1} sx={{ flex: 1, p: 2.5, minWidth: 0, justifyContent: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 800, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            {plan.name}
          </Typography>
          {plan.is_subscribable && (
            <Chip
              icon={<AutorenewIcon sx={{ fontSize: 14 }} />}
              label={t('plan.subscribeMonthly', { ns: 'common', defaultValue: 'Suscripción' })}
              size="small"
              sx={{ height: 22, backgroundColor: 'gold.soft', color: 'gold.main', fontWeight: 700, '& .MuiChip-icon': { color: 'gold.main' } }}
            />
          )}
        </Stack>

        {plan.sessions != null && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {plan.sessions} {t('plan.sessions', { ns: 'common', defaultValue: 'sesiones' })}
          </Typography>
        )}

        {plan.description && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.6,
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
            {plan.description}
          </Typography>
        )}

        {price && (orgBenefit ? (
          <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5 }} flexWrap="wrap">
            <Typography variant="body2" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
              {price}
            </Typography>
            <Typography variant="h6" sx={{ color: 'gold.main', fontWeight: 800 }}>
              {fmtPrice(orgBenefit.discounted_price, orgBenefit.currency)}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="h6" sx={{ color: 'gold.main', fontWeight: 800, mt: 0.5 }}>
            {price}
          </Typography>
        ))}

        {badge && (
          <Box onClick={stop} sx={{ mt: 0.5 }}>{badge}</Box>
        )}

        {actions ? (
          <Box onClick={stop} sx={{ mt: 1 }}>{actions}</Box>
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, color: 'gold.main' }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {t('plan.viewPlan', { ns: 'common', defaultValue: 'Ver plan' })}
            </Typography>
            <ArrowForwardIcon sx={{ fontSize: 16 }} />
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

PlanSplitCard.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    image_url: PropTypes.string,
    price_cop: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    price_usd: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description: PropTypes.string,
    sessions: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    is_subscribable: PropTypes.bool,
    active_discount_percent: PropTypes.number,
    org_benefit: PropTypes.shape({
      discount_percent: PropTypes.number,
      base_price: PropTypes.number,
      discounted_price: PropTypes.number,
      currency: PropTypes.string,
      organization_name: PropTypes.string
    })
  }).isRequired,
  actions: PropTypes.node,
  badge: PropTypes.node
};

export default PlanSplitCard;
